import { ReactElement } from 'react';
import { Box, Heading, Link as LinkComponent, Paragraph } from '@99designs/emu';
import { colors, maxWidths } from '@99designs/emu/settings';
import { FormattedMessage, FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { CurrencyCode } from '@99designs/i18n';
import { FlexibleColumnFAQs, QuestionAnswerProps } from '@99designs/marketing-ui';
import { FAQSchema } from '@99designs/marketing-ui';
import { Price } from '@99designs/marketing-ui';
import { getFormattedPrice, roundAmountInCentsToWholeAmount } from '../../../util/formatPrice';
import type { Price as PriceType } from '../../../util/price';

interface FaqProps {
    faqQuestions: Question[];
    faqHeading: string;
    fromPrice?: PriceType;
    locale?: string;
}

export interface Question {
    id?: string;
    title: string;
    messages: Message[];
    keywords?: string[];
}

export interface Message {
    copy: string;
    links: Link;
}

interface Link {
    [index: string]: string;
}

const getLinkReplacements = (
    message: Message,
    index: number,
    ComponentType: 'a' | typeof LinkComponent
): ReactElement => {
    const replacements: { [key: string]: ReactElement } = {};

    Object.keys(message.links).forEach(
        (key) => (replacements[key] = <ComponentType href={message.links[key]} />)
    );

    return (
        <Box paddingBottom="1.5bu" key={index}>
            <Paragraph>
                <FormattedReactMessage
                    key={index}
                    message={message.copy}
                    replacements={replacements}
                />
            </Paragraph>
        </Box>
    );
};

export interface ItemData {
    id?: string;
    title: string;
    render: () => any;
    keywords?: string[];
    messages: string[];
}

const getSchemaFAQs = (
    questions: Question[],
    fromPrice?: PriceType,
    locale = 'en_US'
): ItemData[] =>
    questions.map((question) => {
        return {
            id: question.id,
            title: question.title,
            messages: question.messages.map((message) => message.copy),
            keywords: question.keywords,
            render() {
                return question.messages.map((message, index) => {
                    if (message.links && !stringContainsPrice(message.copy)) {
                        return getLinkReplacements(message, index, 'a');
                    }
                    if (fromPrice) {
                        return (
                            <Paragraph key={index}>
                                <FormattedMessage
                                    message={message.copy}
                                    values={{
                                        fromPrice: getFormattedPrice(
                                            roundAmountInCentsToWholeAmount(
                                                fromPrice.amountInCents,
                                                fromPrice.currency
                                            ),
                                            fromPrice.currency,
                                            locale
                                        ),
                                        maxLogoPriceInFaqs: getFormattedPrice(
                                            roundAmountInCentsToWholeAmount(
                                                maximumLogoPriceFromFaqsInCents[fromPrice.currency],
                                                fromPrice.currency
                                            ),
                                            fromPrice.currency,
                                            locale
                                        ),
                                    }}
                                />
                            </Paragraph>
                        );
                    }
                    return (
                        <Paragraph>
                            {__(
                                'There’s a wide price range for logos, from almost free to US$10,000 or more. On 99designs, you can get a professionally designed logo from US$299.'
                            )}
                        </Paragraph>
                    );
                });
            },
        };
    });

const splitArray = (question: Question[]) => {
    const result: Question[][] = [[], []];
    question.map((q: Question, index) => {
        if (index < question.length / 2) {
            result[0].push(q);
        } else {
            result[1].push(q);
        }
    });
    return result;
};

const stringContainsPrice = (string: string): boolean => {
    return string.includes('{fromPrice}') || string.includes('{toPrice}');
};

const maximumLogoPriceFromFaqsInCents: Record<CurrencyCode, number> = {
    AUD: 2000000,
    CAD: 2000000,
    CHF: 1000000,
    DKK: 10000000,
    EUR: 1000000,
    GBP: 1000000,
    HKD: 10000000,
    JPY: 1400000,
    MXN: 3000000,
    NOK: 10000000,
    SGD: 2000000,
    USD: 1000000,
};

const getFaqOutput = (question: Question[], fromPrice?: PriceType) => {
    const questions = splitArray(question);
    const output: QuestionAnswerProps[][] = [[], []];
    let keyIndex = 0;
    questions.forEach((qs, i) => {
        qs.forEach(({ id, title, messages }) => {
            const answer = messages.map((message) => {
                keyIndex++;
                if (!stringContainsPrice(message.copy)) {
                    return getLinkReplacements(message, keyIndex, LinkComponent);
                }
                if (fromPrice) {
                    return (
                        <Box paddingBottom="1.5bu" key={keyIndex}>
                            <Paragraph>
                                <FormattedMessage
                                    message={message.copy}
                                    values={{
                                        fromPrice: (
                                            <Price
                                                amount={fromPrice.amountInCents}
                                                currency={fromPrice.currency}
                                            />
                                        ),
                                        maxLogoPriceInFaqs: (
                                            <Price
                                                amount={
                                                    maximumLogoPriceFromFaqsInCents[
                                                        fromPrice.currency
                                                    ]
                                                }
                                                currency={fromPrice.currency}
                                            />
                                        ),
                                    }}
                                />
                            </Paragraph>
                        </Box>
                    );
                }
                return null;
            });
            output[i].push({
                id,
                question: title,
                answer,
            });
        });
    });
    return output;
};

export function FaqSection({ faqQuestions, faqHeading, fromPrice, locale }: FaqProps) {
    const handleFAQOpen = (faq: QuestionAnswerProps) => {
        // TODO: handleFAQOpen to trackEvents on opening the FAQ's
    };
    const schemaFaqs = getSchemaFAQs(faqQuestions, fromPrice, locale);

    return (
        <Box marginY="none" background={colors.grey050} minHeight="600px">
            <Box
                marginLeft="auto"
                marginRight="auto"
                paddingY={['3bu', '3bu', '4bu', '6bu']}
                paddingX={['1bu', '2bu', '2bu', '2bu']}
                maxWidth={`${maxWidths.content.widescreen}px`}
            >
                <Heading
                    appearanceLevel={3}
                    accessibilityLevel={3}
                    fontWeight="normal"
                    color="grey800"
                >
                    {faqHeading}
                </Heading>
                <FAQSchema faqs={schemaFaqs} />
                <Box
                    display="flex"
                    paddingTop={'2bu'}
                    paddingBottom={['1.5bu', '2bu', '2bu', '3bu']}
                    flexDirection={'column'}
                >
                    {faqQuestions.length ? (
                        <FlexibleColumnFAQs
                            onOpen={handleFAQOpen}
                            faqSections={getFaqOutput(faqQuestions, fromPrice)}
                        />
                    ) : null}
                </Box>
            </Box>
        </Box>
    );
}
